import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { PolicyService, PolicyData, PolicyRevision } from '../../services/policy.service';
import { UtilService } from '../../shared/services/util.service';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

export interface PolicyCard {
  key: 'terms' | 'privacy' | 'cookie';
  title: string;
  description: string;
  iconClass: string;
  yamlKey: string;
  currentVersion: string;
  content: string;
  history: PolicyRevision[];
  loading: boolean;
  uploading: boolean;
  selectedFile: File | null;
  selectedFileBase64: string;
  changeNotes: string;
  showAllRevisions: boolean;
}

@Component({
  selector: 'app-compliance-pages',
  templateUrl: './compliance-pages.component.html',
  styleUrls: ['./compliance-pages.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, MatProgressSpinnerModule]
})
export class CompliancePagesComponent implements OnInit {

  cards: { [key in 'terms' | 'privacy' | 'cookie']: PolicyCard } = {
    terms: {
      key: 'terms',
      title: 'Terms & Conditions',
      description: 'Manage and publish your Terms & Conditions document.',
      iconClass: 'fas fa-shield-alt',
      yamlKey: 'terms_and_conditions',
      currentVersion: '1',
      content: '',
      history: [],
      loading: false,
      uploading: false,
      selectedFile: null,
      selectedFileBase64: '',
      changeNotes: '',
      showAllRevisions: false
    },
    privacy: {
      key: 'privacy',
      title: 'Privacy Policy',
      description: 'Manage and publish your Privacy Policy document.',
      iconClass: 'fas fa-lock',
      yamlKey: 'privacy_policy',
      currentVersion: '1',
      content: '',
      history: [],
      loading: false,
      uploading: false,
      selectedFile: null,
      selectedFileBase64: '',
      changeNotes: '',
      showAllRevisions: false
    },
    cookie: {
      key: 'cookie',
      title: 'Cookie Policy',
      description: 'Manage and publish your Cookie Policy document.',
      iconClass: 'fas fa-cookie-bite',
      yamlKey: 'cookie_policy',
      currentVersion: '1',
      content: '',
      history: [],
      loading: false,
      uploading: false,
      selectedFile: null,
      selectedFileBase64: '',
      changeNotes: '',
      showAllRevisions: false
    }
  };

  cardKeys: ('terms' | 'privacy' | 'cookie')[] = ['terms', 'privacy', 'cookie'];

  // Modal preview state
  previewModalOpen: boolean = false;
  previewTitle: string = '';
  previewContent: string = '';

  constructor(
    private policyService: PolicyService,
    private utilService: UtilService,
    private router: Router
  ) { }

  ngOnInit(): void {
    const ur = this.utilService ? this.utilService.decrypt(localStorage.getItem('ur')) : '';
    const useremail = (localStorage.getItem('useremail') || '').toLowerCase();
    const role = (localStorage.getItem('role') || '').toLowerCase();
    const category = (localStorage.getItem('category') || '').toLowerCase();
    const isSuperAdmin = ur === 'SA' || 
                         useremail === 'admin@navriti.com' || 
                         role.includes('super admin') || 
                         role.includes('root admin') || 
                         category.includes('super admin') || 
                         category.includes('root admin');

    if (!isSuperAdmin) {
      this.router.navigate(['/Dashboard']);
      return;
    }

    this.loadAllPolicies();
  }

  loadAllPolicies(): void {
    this.cardKeys.forEach(key => this.loadPolicyData(key));
  }

  loadPolicyData(key: 'terms' | 'privacy' | 'cookie'): void {
    const card = this.cards[key];
    card.loading = true;
    this.policyService.getPolicy(key).subscribe({
      next: (data: PolicyData) => {
        card.loading = false;
        if (data) {
          card.content = data.content;
          card.currentVersion = data.currentVersion || '1';
          card.history = data.history || [];
        }
      },
      error: (err) => {
        card.loading = false;
        console.error(`Error loading ${key} policy:`, err);
      }
    });
  }

  onFileSelected(event: any, key: 'terms' | 'privacy' | 'cookie'): void {
    const file: File = event.target.files[0];
    const card = this.cards[key];
    if (file) {
      if (file.name.toLowerCase().endsWith('.pdf') || !file.name.toLowerCase().endsWith('.docx')) {
        this.utilService.simpleDialog({
          title: 'Invalid File Type',
          message: 'Please upload only .docx documents. PDF files are not allowed.'
        });
        return;
      }

      const maxSizeInBytes = 25 * 1024 * 1024; // 25 MB
      if (file.size > maxSizeInBytes) {
        this.utilService.simpleDialog({
          title: 'File Too Large',
          message: 'The selected document exceeds the 25MB size limit. Please choose a smaller file.'
        });
        return;
      }

      card.selectedFile = file;
      const reader = new FileReader();
      reader.onload = () => {
        card.selectedFileBase64 = reader.result as string;
      };
      reader.readAsDataURL(file);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDrop(event: DragEvent, key: 'terms' | 'privacy' | 'cookie'): void {
    event.preventDefault();
    event.stopPropagation();
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const mockEvent = { target: { files: files } };
      this.onFileSelected(mockEvent, key);
    }
  }

  removeFile(key: 'terms' | 'privacy' | 'cookie'): void {
    const card = this.cards[key];
    card.selectedFile = null;
    card.selectedFileBase64 = '';
  }

  uploadPolicy(key: 'terms' | 'privacy' | 'cookie'): void {
    const card = this.cards[key];
    if (!card.selectedFile || !card.selectedFileBase64) {
      this.utilService.simpleDialog({
        title: 'File Required',
        message: 'Please select a .docx document to upload.'
      });
      return;
    }

    card.uploading = true;
    this.policyService.uploadPolicy(
      key,
      card.selectedFileBase64,
      card.selectedFile.name,
      card.changeNotes
    ).subscribe({
      next: (res) => {
        card.uploading = false;
        card.selectedFile = null;
        card.selectedFileBase64 = '';
        card.changeNotes = '';

        this.utilService.simpleDialog({
          title: 'Success',
          message: `${card.title} document updated and published successfully!`
        });

        this.loadPolicyData(key);
      },
      error: (err) => {
        card.uploading = false;
        console.error(`Error uploading ${key} policy:`, err);
        const errMsg = err?.error?.requestHead?.statusDesc || err?.error?.message || 'Failed to upload policy document.';
        this.utilService.simpleDialog({
          title: 'Upload Failed',
          message: errMsg
        });
      }
    });
  }

  openPreview(key: 'terms' | 'privacy' | 'cookie'): void {
    const card = this.cards[key];
    this.previewTitle = card.title;
    this.previewContent = card.content;
    this.previewModalOpen = true;
  }

  closePreview(): void {
    this.previewModalOpen = false;
  }

  toggleViewAllRevisions(key: 'terms' | 'privacy' | 'cookie'): void {
    const card = this.cards[key];
    card.showAllRevisions = !card.showAllRevisions;
  }

  formatDocName(title: string, version: string): string {
    const cleanTitle = title.replace(/\s+/g, '_');
    return `${cleanTitle}_v${version}.docx`;
  }
}
